// v77.71 — "For professionals" public landing page.
//
// Standalone page targeted at solicitors, mediators, Guardian ad Litems,
// Tusla staff, counsellors and similar. Explains the read-only invite
// flow, what they can/can't see, and the data-isolation guarantees that
// matter most in their context.

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import {
  Scale,
  Briefcase,
  HeartHandshake,
  Stethoscope,
  ShieldCheck,
  Building2,
  Eye,
  ScrollText,
  Lock,
  Clock,
  Users,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, intro, center = false }) => (
  <div className={center ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h2>
    {intro && (
      <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB]">
        {intro}
      </p>
    )}
  </div>
);

const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)] " +
  "hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)] " +
  "transition-shadow duration-200";

const RoleCard = ({ icon: Icon, title, desc, testid }) => (
  <div className={`${cardSurface} p-6`} data-testid={testid}>
    <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
    </div>
    <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h3>
    <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
      {desc}
    </p>
  </div>
);

const StepCard = ({ num, icon: Icon, title, desc, testid }) => (
  <div
    className={`${cardSurface} p-6 flex gap-4 items-start`}
    data-testid={testid}
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#116B6E] text-white inline-flex items-center justify-center font-heading font-semibold text-sm">
      {String(num).padStart(2, "0")}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.8} />
        <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
          {title}
        </h3>
      </div>
      <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
        {desc}
      </p>
    </div>
  </div>
);

const CanRow = ({ ok, label }) => (
  <li className="flex items-start gap-3 text-sm">
    {ok ? (
      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#116B6E] dark:text-[#3FB8BC] flex-shrink-0" strokeWidth={2} />
    ) : (
      <XCircle className="w-4 h-4 mt-0.5 text-[#8A8A8A] dark:text-[#6F828A] flex-shrink-0" strokeWidth={2} />
    )}
    <span className={ok ? "text-[#1A2225] dark:text-[#E8EEF0]" : "text-[#48595E] dark:text-[#A8B6BB] line-through-soft"}>
      {label}
    </span>
  </li>
);

export default function Professionals() {
  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="professionals-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="professionals-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="professionals-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="professionals-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16">
          <Eyebrow>For professionals</Eyebrow>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05] font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            Read-only access for solicitors, mediators and family-support professionals
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            When a parent invites you to CoParent Console, you get a clean,
            time-stamped, view-only window into the records that relate to the
            specific child the parent grants you access to — and nothing more.
            No sign-up fees. No installation. No surprises.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" data-testid="professionals-cta-contact" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-7 text-base font-medium">
                Get in touch
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/about" data-testid="professionals-cta-about" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto h-12 px-7 text-base rounded-md border-[#D6F0EF] dark:border-[#2A3539] bg-white/60 dark:bg-[#161E21]"
              >
                Learn about CoParent Console
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]" data-testid="professionals-roles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Who it's for"
            title="Built around the way professionals already work"
            intro="The platform was developed in consultation with professionals working directly within the Irish system — so the access model fits real-world casework rather than fighting it."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10">
            <RoleCard icon={Scale} title="Solicitors" desc="View structured records when preparing or progressing a family-law matter." testid="professionals-role-solicitors" />
            <RoleCard icon={HeartHandshake} title="Mediators" desc="See the agreed schedule, communications and finances during sessions — neutral, time-stamped." testid="professionals-role-mediators" />
            <RoleCard icon={Briefcase} title="Guardian ad Litems" desc="Access the child-centred record set you need, without dragging the parent through paperwork." testid="professionals-role-gal" />
            <RoleCard icon={Stethoscope} title="Counsellors &amp; play therapists" desc="Pull context from a calm record of routines, events and parental communication." testid="professionals-role-counsellors" />
            <RoleCard icon={Building2} title="Tusla staff" desc="Receive view-only access from a parent — useful for assessments and family-support work." testid="professionals-role-tusla" />
            <RoleCard icon={ShieldCheck} title="An Garda Síochána" desc="When a parent shares records as part of an enquiry, the chain-of-custody is clear." testid="professionals-role-garda" />
          </div>
        </div>
      </section>

      {/* HOW THE INVITE FLOW WORKS */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="professionals-flow">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="How invites work"
            title="A controlled, granter-led process"
            intro="Access begins with the parent, stays with the parent, and can be revoked by the parent at any time."
          />
          <div className="mt-10 space-y-4">
            <StepCard
              num={1}
              icon={Users}
              title="A parent issues an invite"
              desc="From their CoParent Console account, the parent generates an invite for a specific child. The invite is single-use and time-limited."
              testid="professionals-step-issue"
            />
            <StepCard
              num={2}
              icon={Eye}
              title="You accept and create a view-only account"
              desc="One click on the invite link sets up your account. No card details, no payment. You see only what the parent granted access to."
              testid="professionals-step-accept"
            />
            <StepCard
              num={3}
              icon={ScrollText}
              title="You read structured, time-stamped records"
              desc="Messages, calendar, expenses, journal, child profile and uploaded documents — all scoped to the specific child the parent shared with you."
              testid="professionals-step-read"
            />
            <StepCard
              num={4}
              icon={Clock}
              title="Access ends when the parent decides"
              desc="The parent can revoke at any time. Invites also have an expiry built-in. Nothing lingers after that."
              testid="professionals-step-end"
            />
          </div>
        </div>
      </section>

      {/* WHAT YOU CAN / CANNOT DO */}
      <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]" data-testid="professionals-permissions">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="What you can &amp; can't do"
                title="View-only by design"
              />
              <p className="mt-4 text-sm text-[#48595E] dark:text-[#A8B6BB] leading-relaxed">
                Professional access is read-only by default. The aim is to keep
                the record neutral — you observe, you don&apos;t alter.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              <div className={`${cardSurface} p-6`} data-testid="professionals-can-list">
                <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={2} />
                  You can
                </h3>
                <ul className="mt-4 space-y-2.5">
                  <CanRow ok label="Read messages between the parents" />
                  <CanRow ok label="View the shared calendar and parenting schedule" />
                  <CanRow ok label="See expenses, splits and settlement history" />
                  <CanRow ok label="Read shared journal entries and documents" />
                  <CanRow ok label="View the child's profile (medical, school, routines)" />
                  <CanRow ok label="Download structured PDF exports for casework" />
                </ul>
              </div>
              <div className={`${cardSurface} p-6`} data-testid="professionals-cannot-list">
                <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9] flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-[#8A8A8A] dark:text-[#6F828A]" strokeWidth={2} />
                  You can&apos;t
                </h3>
                <ul className="mt-4 space-y-2.5">
                  <CanRow ok={false} label="Edit, delete or alter records in any way" />
                  <CanRow ok={false} label="Send messages on the parent's behalf" />
                  <CanRow ok={false} label="See data about other children you weren't granted access to" />
                  <CanRow ok={false} label="See other professionals invited to the same household" />
                  <CanRow ok={false} label="Continue accessing data after the parent revokes" />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY GUARANTEES */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="professionals-privacy">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Privacy &amp; control"
            title="The parent stays in control"
          />
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-10">
            <RoleCard
              icon={Eye}
              title="Granter-only visibility"
              desc="You only see records the inviting parent has shared — never anything from the other parent's separate scope."
              testid="professionals-priv-granter"
            />
            <RoleCard
              icon={Lock}
              title="Per-child scoping"
              desc="If a parent shares one child with you, that's the boundary. Other children in the household stay invisible."
              testid="professionals-priv-perchild"
            />
            <RoleCard
              icon={Clock}
              title="Time-limited &amp; revocable"
              desc="Invites expire on a date the parent picks, and they can revoke instantly at any time from Settings."
              testid="professionals-priv-revocable"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-b from-[#F4FAFA] to-white dark:from-[#0F2F30]/30 dark:to-[#0E1416] border-y border-[#E5EBEC] dark:border-[#1F2A2D] overflow-hidden" data-testid="professionals-cta">
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] text-[#1A2225] dark:text-[#F4F8F9]">
            Want to learn more, or refer a parent?
          </h2>
          <p className="mt-4 text-base text-[#48595E] dark:text-[#A8B6BB] max-w-xl mx-auto">
            We&apos;re happy to walk you through the platform, share an example
            export, or answer any questions about the privacy model.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
            <Link to="/contact" data-testid="professionals-final-contact" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-8 text-base font-medium shadow-sm transition-colors">
                Contact us
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/help" data-testid="professionals-final-help" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto h-12 px-8 text-base rounded-md border-[#D6F0EF] dark:border-[#2A3539] bg-white dark:bg-[#161E21]"
              >
                Read the help centre
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">CoParent Console</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/help" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Help</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
